"use client";

import { useEffect } from "react";

/**
 * Global error handler that detects Server Action errors caused by deployment mismatches
 * and automatically reloads the page to fetch fresh client code.
 * 
 * This prevents infinite error loops when users have stale cached code trying to call
 * Server Actions that no longer exist after a new deployment.
 */
export default function ServerActionErrorHandler() {
    useEffect(() => {
        // Check if we've already reloaded in this session to prevent infinite reload loops
        const hasReloaded = sessionStorage.getItem("wexba_server_action_reload");

        if (hasReloaded) {
            // Already reloaded once, don't reload again
            return;
        }

        const handleError = (event: ErrorEvent) => {
            const errorMessage = event.error?.message || event.message || "";

            // Detect Server Action errors
            if (errorMessage.includes("Failed to find Server Action")) {
                console.warn("🔄 [ServerActionErrorHandler] Detected stale Server Action. Reloading page...");

                // Mark that we're reloading to prevent infinite loops
                sessionStorage.setItem("wexba_server_action_reload", "true");

                // Reload the page to get fresh code
                window.location.reload();
            }
        };

        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            const errorMessage = event.reason?.message || String(event.reason) || "";

            // Detect Server Action errors in promise rejections
            if (errorMessage.includes("Failed to find Server Action")) {
                console.warn("🔄 [ServerActionErrorHandler] Detected stale Server Action in promise. Reloading page...");

                // Mark that we're reloading to prevent infinite loops
                sessionStorage.setItem("wexba_server_action_reload", "true");

                // Reload the page to get fresh code
                window.location.reload();
            }
        };

        // Listen for both error events and unhandled promise rejections
        window.addEventListener("error", handleError);
        window.addEventListener("unhandledrejection", handleUnhandledRejection);

        // Cleanup listeners on unmount
        return () => {
            window.removeEventListener("error", handleError);
            window.removeEventListener("unhandledrejection", handleUnhandledRejection);
        };
    }, []);

    // This component doesn't render anything
    return null;
}
